import type { DeathCause, EffectModifiers } from "odyssey-shared";
import { say } from "../../words.ts";

/**
 * The wording of a death's penalty (P2.2-T23, P2.5-T7; ADR-079's amendment of 2026-10-04, item 7): the effect's
 * tooltip lines (`bar/describe.ts`) and the death screen's cost line (`cost.ts`). The words are the language files'
 * (`death.*` in `odyssey-lang`), in one place, so an edit of the author's is one change.
 */

/**
 * The modifiers a penalty's cost names, in the order the cost lists them.
 */
export const PENALTY_MODIFIERS: readonly (keyof EffectModifiers & ("damageDealt" | "damageTaken" | "maxHealth" | "cooldown"))[] = ["damageDealt", "damageTaken", "maxHealth", "cooldown"];

/**
 * What a modifier is called in a penalty's cost.
 *
 * @param modifier The modifier.
 * @returns Its words, "damage dealt".
 */
export function penaltyModifierWords(modifier: (typeof PENALTY_MODIFIERS)[number]): string {
  return say(`death.modifiers.${modifier}`);
}

/**
 * The tooltip line that says which death a penalty follows.
 *
 * @param cause The death.
 * @returns The line.
 */
export function penaltyFollows(cause: DeathCause): string {
  return say("death.follows", { death: say(`death.deaths.${cause}`) });
}

/**
 * The tooltip line that counts a penalty's stacks.
 *
 * @param held The stacks the character holds.
 * @param most The most it can hold.
 * @returns The line.
 */
export function penaltyStacks(held: number, most: number): string {
  return say("death.stacks", { held: String(held), most: String(most) });
}

/**
 * One part of a penalty's cost.
 *
 * @param percent The change, signed, as a percentage.
 * @param words What it changes.
 * @returns The part, "-10% damage dealt".
 */
export function penaltyModifier(percent: string, words: string): string {
  return say("death.modifier", { percent, words });
}

/**
 * A penalty's cost on the death screen.
 *
 * @param name The effect's name.
 * @param parts The cost's parts.
 * @returns The line.
 */
export function penaltyCost(name: string, parts: readonly string[]): string {
  return say("death.cost", { name, parts: parts.join(", ") });
}
